import { useContext } from "react";
import ComponentPropertiesContext from "../../Context/Display/ComponentProperties/ComponentPropertiesContext";
import { UPDATE_CHANGE_TRACK_PROPERTY, UPDATE_GENERAL_PROPERTY, UPDATE_FETCHED_VALUE, TOGGLE_CREATE_ACTION_DIALOG, ADD_NEW_ACTION, SELECTED_STEP_ID, SET_ACTION_TABLE_ACTIONS, REMOVE_MULTIPLE_STEPS, TOGGLE_EXPAND_ACTION, SET_FORM_ACTIONS, UPDATE_FORM_ACTION, TOGGLE_DELETE_ACTION_DIALOG, DELETE_ACTION, TOGGLE_UPDATE_ACTION_DIALOG, UPDATE_ACTION, SET_ATTRIBUTE_TABLE_ATTRIBUTES, TOGGLE_SELECT_ATTRIBUTE_DIALOG, TOGGLE_ATTRIBUTE_HYPERLINK, SELECT_ATTRIBUTE, SET_ATTRIBUTE_TABLE_TARGET_PAGE, SET_ATTRIBUTE_PROPERTY, SET_REFERENCES, UPDATE_REFERENCE_PROPERTY, SELECT_REFERENCE, ADD_REFERENCE_ROW, TOGGLE_SYNC_ATTRIBUTE_DIALOG, SET_SYNCED_COMPONENTS_CHANGED, RESIZE_COLUMNS, MOVE_SECTION_UP, MOVE_SECTION_DOWN, REMOVE_SECTION, ADD_SECTION, UPDATE_SECTION_NAME, UPDATE_SECTION_LANGUAGES, REMOVE_COLUMN, DROP_COLUMN_IN_SAME_SECTION, DROP_COLUMN_IN_DIFFERENT_SECTION, ADD_COLUMN, SET_STEPS, ADD_STEP, REMOVE_STEP, UPDATE_STEP_NAME, UPDATE_STEP_LANGUAGES, TOGGLE_COLUMN_HYPERLINK, TOGGLE_COLUMN_COLOR_FORMATTING, UPDATE_LAYOUT_FROM_RECOMMENDATION, SET_FORM_BUILDER_ATTRIBUTES, UPDATE_FORM_BUILDER_ATTRIBUTE_PROPERTY, SELECT_FORM_BUILDER_ATTRIBUTE, TOGGLE_FORM_BUILDER_SYNC_ATTRIBUTE_DIALOG, TOGGLE_EDIT_COLUMN_PANEL, SET_EDITING_COLUMN, UPDATE_COLUMN_SETTINGS, SET_VALIDATION_ERRORS, CLEAR_VALIDATION_ERRORS } from "../../Context/Display/ComponentProperties/ComponentsPropertiesEvents";
import componentPropertiesMap from "../../Components/Display/EditComponentForm/ComponentFormProperties.json";
import { useSelectedComponent } from "./useComponents";
import usePages, { usePortalPages, useSelectedPage } from "./usePages";
import { syncGroupAttributesWithComponents, syncViewAttributesWithComponents, updateComponent, updateDatePropertiesRelatedToGanttChart } from "../../API/Display/Display";
import { getIdBasedGeneralProperties, uuidv4 } from "./utils";
import { getPageByProperty } from "../../Components/Display/utils";
import { UPDATE_PAGE_COMPONENT } from "../../Context/Display/Pages/PagesContextEvents";
import { getComponentByProperty } from "../../Components/Display/Components/utils";
import { extraStepsPropertiesForDB, extractLayoutPropertiesForDB } from "../../Context/Display/ComponentProperties/utils";
import { getAttributes } from "../../API/Attribute/Attribute";

function useComponentPropertiesContext() {
    const [state, dispatch] = useContext(ComponentPropertiesContext);
    return [state, dispatch];
}

export function useComponentProperties() {
    const [state, dispatch] = useComponentPropertiesContext();
    const { generalProperties } = state;

    function updateGeneralProperties(property, value) {
        dispatch({
            type: UPDATE_GENERAL_PROPERTY,
            payload: {
                property,
                value
            }
        })
    }
    return [generalProperties, updateGeneralProperties];
}

export function useResetComponentProperties() {
    const [, dispatch] = useComponentPropertiesContext();

    function resetContext() {
        dispatch({
            type: "RESET_CONTEXT"
        })
    }

    return resetContext;
}

export function useActionsDispatch() {
    const [, dispatch] = useActions();
    return dispatch;
}

export function useActions() {

    const [state, dispatch] = useComponentPropertiesContext();
    const { actionTable } = state;

    function toggleCreateActionDialog(id) {
        dispatch({ type: TOGGLE_CREATE_ACTION_DIALOG, payload: id });
    }

    function toggleUpdateActionDialog(id) {
        dispatch({ type: TOGGLE_UPDATE_ACTION_DIALOG, payload: id });
    }

    function addNewAction(action) {
        dispatch({
            type: ADD_NEW_ACTION,
            payload: action
        })
    }

    function updateAction(id, properties) {
        dispatch({
            type: UPDATE_ACTION,
            payload: {
                id,
                properties
            }
        })
    }

    function setActions(actions) {
        dispatch({
            type: SET_ACTION_TABLE_ACTIONS,
            payload: actions
        })
    }

    function toggleExpanded(id) {
        dispatch({
            type: TOGGLE_EXPAND_ACTION,
            payload: id
        })
    }

    function deleteAction(id) {
        dispatch({
            type: DELETE_ACTION,
            payload: id
        })
    }

    return [
        actionTable,
        {
            toggleCreateActionDialog,
            toggleUpdateActionDialog,
            addNewAction,
            setActions,
            toggleExpanded,
            deleteAction,
            updateAction
        }
    ];
}

export function useResetActions() {
    const selectedComponent = useSelectedComponent();
    const [, dispatch] = useActions();

    function resetActions() {
        dispatch.setActions(selectedComponent.actions);
    }

    return resetActions;
}

export function useLayout() {
    const [{ steps, selectedStepId }, dispatch] = useAttributeFormBuilder();
    let selectedStep = steps.find(step => step.id === selectedStepId);
    if (!selectedStep) {
        dispatch.setSelectedStep(steps[0].id)
        selectedStep = steps[0];
    }
    const layout = selectedStep.layout;
    return layout;
}

export function useAttributeFormBuilder() {
    const [state, dispatch] = useComponentPropertiesContext();

    const { attributeFormBuilder } = state;

    function setSteps(steps) {
        dispatch({
            type: SET_STEPS,
            payload: steps
        })
    }

    function setSelectedStep(id) {
        dispatch({
            type: SELECTED_STEP_ID,
            payload: id
        })
    }

    function addStep() {
        dispatch({
            type: ADD_STEP
        })
    }

    function removeStep() {
        dispatch({
            type: REMOVE_STEP
        })
    }

    function updateStepName(name) {
        dispatch({
            type: UPDATE_STEP_NAME,
            payload: name
        })
    }

    function updateStepLanguages(languages) {
        dispatch({
            type: UPDATE_STEP_LANGUAGES,
            payload: languages
        })
    }

    function setFormActions(formActions) {
        dispatch({
            type: SET_FORM_ACTIONS,
            payload: formActions
        })
    }

    function updateFormAction(formAction) {
        dispatch({
            type: UPDATE_FORM_ACTION,
            payload: formAction
        })
    }

    function removeMultipleSteps() {
        dispatch({
            type: REMOVE_MULTIPLE_STEPS,
        })
    }

    function addColum(columns, dropSectionId, draggedAttribute, componentType) {
        dispatch({
            type: ADD_COLUMN,
            payload: { columns, dropSectionId, draggedAttribute, componentType }
        })
    }

    function removeColumn(attributeId) {
        dispatch({
            type: REMOVE_COLUMN,
            payload: attributeId
        })
    }

    function toggleColumnHyperlink(attributeId, hyperlink, targetPageId) {
        dispatch({
            type: TOGGLE_COLUMN_HYPERLINK,
            payload: { attributeId, hyperlink, targetPageId }
        })
    }

    function toggleColumnColorFormatting(attributeId, cellRules) {
        dispatch({
            type: TOGGLE_COLUMN_COLOR_FORMATTING,
            payload: { attributeId, cellRules }
        })
    }

    function dropColumnInSameSection(columns, dropSectionId, draggedColumn) {
        dispatch({
            type: DROP_COLUMN_IN_SAME_SECTION,
            payload: { columns, dropSectionId, draggedColumn }
        })
    }

    function dropColumnInDifferentSection(columns, dragSectionId, dropSectionId, draggedColumn) {
        dispatch({
            type: DROP_COLUMN_IN_DIFFERENT_SECTION,
            payload: { columns, dragSectionId, dropSectionId, draggedColumn }
        })
    }

    function resizeColumns(columns, sectionId) {
        dispatch({
            type: RESIZE_COLUMNS,
            payload: { columns, sectionId }
        })
    }

    function addSection() {
        dispatch({
            type: ADD_SECTION
        })
    }

    function removeSection(sectionId) {
        dispatch({
            type: REMOVE_SECTION,
            payload: sectionId
        })
    }

    function moveSectionUp(sectionId) {
        dispatch({
            type: MOVE_SECTION_UP,
            payload: sectionId
        })
    }

    function moveSectionDown(sectionId) {
        dispatch({
            type: MOVE_SECTION_DOWN,
            payload: sectionId
        })
    }

    function updateSectionName(section, sectionId) {
        dispatch({
            type: UPDATE_SECTION_NAME,
            payload: { section, sectionId }
        })
    }

    function updateSectionLanguages(languages, sectionId) {
        dispatch({
            type: UPDATE_SECTION_LANGUAGES,
            payload: { languages, sectionId }
        })
    }

    function toggleEditColumnPanel() {
        dispatch({
            type: TOGGLE_EDIT_COLUMN_PANEL
        })
    }

    function setEditingColumn(column) {
        dispatch({
            type: SET_EDITING_COLUMN,
            payload: column
        })
    }

    function updateColumnSettings(attributeId, columnSettings) {
        dispatch({
            type: UPDATE_COLUMN_SETTINGS,
            payload: { attributeId, columnSettings }
        })
    }

    return [
        attributeFormBuilder,
        {
            setSteps,
            setSelectedStep,
            addStep,
            removeStep,
            updateStepName,
            updateStepLanguages,
            setFormActions,
            updateFormAction,
            removeMultipleSteps,
            addColum,
            removeColumn,
            toggleColumnHyperlink,
            toggleColumnColorFormatting,
            dropColumnInSameSection,
            dropColumnInDifferentSection,
            resizeColumns,
            addSection,
            removeSection,
            moveSectionUp,
            moveSectionDown,
            updateSectionName,
            updateSectionLanguages,
            toggleEditColumnPanel,
            setEditingColumn,
            updateColumnSettings
        }
    ];
}

export function useResetAttributeFormBuilder() {
    const selectedComponent = useSelectedComponent();
    const [, dispatch] = useAttributeFormBuilder();

    function resetLayout() {
        const steps = selectedComponent.steps.slice();
        const formActions = selectedComponent.formActions.slice();
        dispatch.setSteps(steps);
        dispatch.setFormActions(formActions);
        dispatch.setSelectedStep(steps[0].id);
    }
    return resetLayout;
}

export function useAttributes() {
    const selectedComponent = useSelectedComponent();
    const [fetchedValue] = useFetchedValues();

    const [state, dispatch] = useComponentPropertiesContext();
    const { attributeTable } = state;

    function setAttributes(attributes) {
        dispatch({
            type: SET_ATTRIBUTE_TABLE_ATTRIBUTES,
            payload: attributes
        })
    }

    function toggleSelectAttributeDialog() {
        dispatch({
            type: TOGGLE_SELECT_ATTRIBUTE_DIALOG
        })
    }

    function toggleAttributeHyperlink(id) {
        dispatch({
            type: TOGGLE_ATTRIBUTE_HYPERLINK,
            payload: id
        })
    }

    function selectAttributeRow(id) {
        dispatch({
            type: SELECT_ATTRIBUTE,
            payload: id
        })
    }

    function setAttributeProperty(attributeId, property, value) {
        dispatch({
            type: SET_ATTRIBUTE_PROPERTY,
            payload: {
                attributeId,
                property,
                value
            }
        })
    }

    function toggleSyncAttributeDialog() {
        dispatch({
            type: TOGGLE_SYNC_ATTRIBUTE_DIALOG
        })
    }

    function setSyncedComponentsChanged(value) {
        dispatch({
            type: SET_SYNCED_COMPONENTS_CHANGED,
            payload: value
        })
    }

    function updateLayoutFromRecommendation(attributes) {
        dispatch({
            type: UPDATE_LAYOUT_FROM_RECOMMENDATION,
            payload: attributes
        })
    }

    return [
        attributeTable,
        {
            setAttributes,
            toggleSelectAttributeDialog,
            toggleAttributeHyperlink,
            selectAttributeRow,
            setAttributeProperty,
            toggleSyncAttributeDialog,
            setSyncedComponentsChanged,
            updateLayoutFromRecommendation
        }
    ];
}

export function useFormBuilderAttributes() {
    const [state, dispatch] = useComponentPropertiesContext();
    const { formBuilderAttributes } = state;

    function setFormBuilderAttributes(attributes) {
        dispatch({
            type: SET_FORM_BUILDER_ATTRIBUTES,
            payload: attributes
        })
    }

    function updateFormBuilderAttributeProperty(attributeId, property, value) {
        dispatch({
            type: UPDATE_FORM_BUILDER_ATTRIBUTE_PROPERTY,
            payload: {
                attributeId,
                property,
                value
            }
        })
    }

    function selectFormBuilderAttribute(id) {
        dispatch({
            type: SELECT_FORM_BUILDER_ATTRIBUTE,
            payload: id
        })
    }

    function toggleFormBuilderSyncAttributeDialog() {
        dispatch({
            type: TOGGLE_FORM_BUILDER_SYNC_ATTRIBUTE_DIALOG
        })
    }

    function updateLayoutFromRecommendation(attributes) {
        dispatch({
            type: UPDATE_LAYOUT_FROM_RECOMMENDATION,
            payload: attributes
        })
    }

    return [
        formBuilderAttributes,
        {
            setFormBuilderAttributes,
            updateFormBuilderAttributeProperty,
            selectFormBuilderAttribute,
            toggleFormBuilderSyncAttributeDialog,
            updateLayoutFromRecommendation
        }
    ];
}

export function useResetAttributeTable() {
    const selectedComponent = useSelectedComponent();
    const [fetchedValue] = useFetchedValues();
    const [, dispatch] = useAttributes();

    function resetAttributes() {
        const attributesTableData = selectedComponent.attributes.map(attribute => {
            const selectedAttribute = fetchedValue.attributeTable.attributes.find(attr => attr.id === attribute.attributeId);
            return {
                ...attribute,
                attributeName: selectedAttribute.attributeName,
                attributeLabel: selectedAttribute.attributeLabel,
                attributeType: selectedAttribute.attributeType,
                entityId: selectedAttribute.entityId,
            }
        });
        dispatch.setAttributes(attributesTableData);
    }

    return resetAttributes;
}

export function useReferences() {
    const [state, dispatch] = useComponentPropertiesContext();
    const { referenceTable } = state;

    function setReferences(references) {
        dispatch({
            type: SET_REFERENCES,
            payload: references
        })
    }

    function selectReference(id) {
        dispatch({
            type: SELECT_REFERENCE,
            payload: id
        })
    }

    function addNewReferenceRow() {
        dispatch({
            type: ADD_REFERENCE_ROW
        })
    }

    function updateReferenceProperty(id, property, value) {
        dispatch({
            type: UPDATE_REFERENCE_PROPERTY,
            payload: {
                id,
                property,
                value
            }
        })
    }

    function deleteReference(id) {
        dispatch({
            type: "DELETE_REFERENCE",
            payload: id
        })
    }

    return [
        referenceTable,
        {
            setReferences,
            selectReference,
            addNewReferenceRow,
            updateReferenceProperty,
            deleteReference
        }
    ];
}

export function useChangeBooleans() {
    const [state, dispatch] = useComponentPropertiesContext();
    const {
        isGeneralPropertiesChanged,
        isAttributeFormBuilderChanged,
        isActionTableChanged,
        isAttributeTableChanged,
        isReferenceTableChanged
    } = state.changeTrack;

    function updateChangeBooleans(property, value) {
        dispatch({
            type: UPDATE_CHANGE_TRACK_PROPERTY,
            payload: {
                property,
                value
            }
        })
    }

    return [
        { isGeneralPropertiesChanged, isAttributeFormBuilderChanged, isActionTableChanged, isAttributeTableChanged, isReferenceTableChanged },
        updateChangeBooleans
    ];
}

export function useFetchedValues() {
    const [state, dispatch] = useComponentPropertiesContext();
    const { fetchedValues } = state;

    function updateFetchedValues(property, value) {
        dispatch({
            type: UPDATE_FETCHED_VALUE,
            payload: {
                property,
                value
            }
        })
    }

    function updateGeneralFetchedValue(property, value) {
        dispatch({
            type: UPDATE_FETCHED_VALUE,
            payload: {
                key: "generalProperties",
                property,
                value
            }
        })
    }

    function updateAttributeTableFetchedValue(property, value) {
        dispatch({
            type: UPDATE_FETCHED_VALUE,
            payload: {
                key: "attributeTable",
                property,
                value
            }
        })
    }

    function updateActionTableFetchedValue(property, value) {
        dispatch({
            type: UPDATE_FETCHED_VALUE,
            payload: {
                key: "actionTable",
                property,
                value
            }
        })
    }

    function updateReferenceTableFetchedValue(property, value) {
        dispatch({
            type: UPDATE_FETCHED_VALUE,
            payload: {
                key: "referenceTable",
                property,
                value
            }
        })
    }

    return [
        fetchedValues,
        {
            updateFetchedValues,
            updateGeneralFetchedValue,
            updateAttributeTableFetchedValue,
            updateActionTableFetchedValue,
            updateReferenceTableFetchedValue
        }
    ];
}

// This hooks deals with most of save scenarios
// For other calls, please manage them in the component
export function useSaveComponent() {
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();

    const [changeProperties] = useChangeBooleans();
    const [properties] = useComponentProperties();
    const [{ actions }] = useActions();
    const [{ attributes, isSyncedComponentsChanged }] = useAttributes();
    const [{ steps, formActions, formBuilderLoading }] = useAttributeFormBuilder();
    const [{ references }] = useReferences();

    function populateAttributesToStep(step, attributesToPopulate, allEntityAttributes) {
        try {
            for (let i = 0; i < attributesToPopulate.length; i++) {
                const attribute = attributesToPopulate[i];
                const originalAttribute = allEntityAttributes.find(attr => attr._id === attribute.attributeId);
                let segmentColor = [];
                if (originalAttribute && originalAttribute.attributeType === "Picklist String" && originalAttribute.picklist) {
                    const picklistValues = originalAttribute.picklist.split('ɖ').filter(value => value.trim() !== '');
                    segmentColor = picklistValues.map(() => ({ textColor: "#ffffff", fillColor: "#000000" }));
                }
                step.layout[0].columns.push({
                    i: uuidv4(),
                    attributeId: attribute.attributeId,
                    attributeType: attribute.attributeType,
                    referencedEntityId: attribute.referencedEntityId,
                    cellRules: false,
                    x: 0,
                    y: i,
                    w: 12,
                    h: attribute.attributeType === "Multiline Text" ? 2 : 1,
                    minW: 1,
                    maxW: 12,
                    minH: attribute.attributeType === "Multiline Text" ? 2 : 1,
                    columnSettings: {
                        displayType: attribute.attributeType === "Picklist String" ? "SegmentedControl" : "Inherit",
                        fontSize: 14,
                        fontWeight: "regular",
                        placeholder: "",
                        segmentColor: segmentColor,
                        cellRules: false,
                        showLabel: selectedComponent.componentType === "Cards" ? true : false,
                        hyperlink: attribute.hyperlink || false,
                        targetPageId: attribute.targetPageId || ""
                    }
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    function propagateFieldActionsToSteps(stepsToUpdate, actionsToApply) {
        for (const step of stepsToUpdate) {
            if (!step.layout) continue;
            for (const section of step.layout) {
                if (!section.columns) continue;
                for (const column of section.columns) {
                    column.columnSettings = {
                        ...(column.columnSettings || {}),
                        ...actionsToApply
                    };
                }
            }
        }
    }

    function getComponentType() {
        // convert above to switch case
        if (!selectedPage || !selectedPage._id) return "";
        if (!selectedComponent) return "";
        switch (selectedComponent.componentType) {
            case "Heading":
                if (selectedPage.pageType === "Entity Group") {
                    return "GroupHeading";
                }
                return "SpecificHeading";
            case "ProgressBar":
                return "Progress Bar";
            case "Tab Container":
                return "TabContainer";
            case "Split Section":
                return "SplitSection";
            default:
                return selectedComponent.componentType;
        }
    }

    const componentType = getComponentType();
    const formProperties = componentPropertiesMap[componentType];

    async function save() {
        let updatedComponent = {};
        const newProperties = {};
        if (componentType === "Cards") {
            //
        }
        // Determine which form-level Field Action toggles changed in this save cycle.
        // Each entry maps form-level Form property -> per-column columnSettings key.
        const fieldActionPropagationMap = {
            fieldActionReset: "actionReset",
            fieldActionComment: "actionComment",
            fieldActionHistory: "actionHistory",
            fieldActionAttachment: "actionAttachment"
        };
        const fieldActionsToPropagate = {};
        if (changeProperties.isGeneralPropertiesChanged && (componentType === "Form" || componentType === "View")) {
            for (const formProp of Object.keys(fieldActionPropagationMap)) {
                const newValue = Boolean(properties[formProp]);
                const oldValue = Boolean(selectedComponent[formProp]);
                if (newValue !== oldValue) {
                    fieldActionsToPropagate[fieldActionPropagationMap[formProp]] = newValue;
                }
            }
        }
        if (changeProperties.isGeneralPropertiesChanged) {
            for (const property of formProperties) {
                newProperties[property] = properties[property];
            }
            const idProperties = getIdBasedGeneralProperties(componentType, properties);
            Object.keys(idProperties)
                .forEach(key => newProperties[key] = idProperties[key]);
        }
        if (changeProperties.isAttributeFormBuilderChanged) {
            if (Object.keys(fieldActionsToPropagate).length > 0) {
                propagateFieldActionsToSteps(steps, fieldActionsToPropagate);
            }
            newProperties["steps"] = extraStepsPropertiesForDB(steps);
            newProperties["formActions"] = formActions;
        } else if (Object.keys(fieldActionsToPropagate).length > 0) {
            // Form Builder slice is empty until the tab mounts. If the user
            // toggles field actions without opening that tab, fall back to the
            // authoritative copy on selectedComponent so we don't write [].
            const builderNotLoaded = formBuilderLoading || !steps || steps.length === 0;
            const baseSteps = builderNotLoaded
                ? structuredClone(selectedComponent.steps || [])
                : steps;
            const baseFormActions = builderNotLoaded
                ? (selectedComponent.formActions || [])
                : formActions;
            propagateFieldActionsToSteps(baseSteps, fieldActionsToPropagate);
            newProperties["steps"] = extraStepsPropertiesForDB(baseSteps);
            newProperties["formActions"] = baseFormActions;
        } else {
            const isFormBuilderBlank = !steps || steps.length === 0 ||
                (steps.length === 1 &&
                    (!steps[0].layout ||
                        steps[0].layout.length === 0 ||
                        (steps[0].layout.length === 1 &&
                            steps[0].layout[0].columns.length === 0)));

            if (isFormBuilderBlank && selectedComponent.componentType === "Cards" && properties.inlineEdit && !selectedComponent.inlineEdit) {
                const allEntityAttributes = await getAttributes(selectedPage.entityId);
                const attributesToPopulate = (attributes && attributes.length > 0 ? attributes.slice(0, 4) : (selectedComponent.attributes || []).slice(0, 4));

                const emptyStep = {
                    id: uuidv4(),
                    name: "Step 1",
                    layout: [{
                        id: uuidv4(),
                        section: "",
                        columns: []
                    }],
                    languages: {}
                };

                if (attributesToPopulate.length > 0) {
                    populateAttributesToStep(emptyStep, attributesToPopulate, allEntityAttributes);
                }

                newProperties["steps"] = [emptyStep];
                newProperties["formActions"] = selectedComponent.formActions || [];
            }
        }
        if (changeProperties.isAttributeTableChanged) {
            newProperties["attributes"] = attributes;
        }
        if (changeProperties.isActionTableChanged) {
            newProperties["actions"] = actions;
        }
        if (changeProperties.isReferenceTableChanged) {
            newProperties["referredPages"] = getReferencesInDbFormat();
        }
        if (Object.keys(newProperties).length > 0) {
            if (
                Array.isArray(newProperties.steps) &&
                newProperties.steps.length === 0 &&
                Array.isArray(selectedComponent.steps) &&
                selectedComponent.steps.length > 0
            ) {
                console.warn("Refused to overwrite non-empty steps with []");
                delete newProperties.steps;
                delete newProperties.formActions;
            }
            await updateComponent(selectedPage._id, selectedComponent.id, newProperties);
            if (componentType === "Gantt Chart") {
                if (selectedComponent.startDateAttributeId && newProperties.startDateAttributeId !== selectedComponent.startDateAttributeId) {
                    await updateDatePropertiesRelatedToGanttChart(selectedComponent.startDateAttributeId, true, 0);
                }
                if (selectedComponent.endDateAttributeId && newProperties.endDateAttributeId !== selectedComponent.endDateAttributeId) {
                    await updateDatePropertiesRelatedToGanttChart(selectedComponent.endDateAttributeId, true, 3);
                }
                if (newProperties.startDateAttributeId && newProperties.startDateAttributeId !== selectedComponent.startDateAttributeId) {
                    await updateDatePropertiesRelatedToGanttChart(newProperties.startDateAttributeId, true, 0);
                }
                if (newProperties.endDateAttributeId && newProperties.endDateAttributeId !== selectedComponent.endDateAttributeId) {
                    await updateDatePropertiesRelatedToGanttChart(newProperties.endDateAttributeId, true, 3);
                }
            }
            updatedComponent = {
                ...selectedComponent,
                ...newProperties
            };
        }
        if (
            (
                isSyncedComponentsChanged
            ) ||
            (
                selectedComponent.componentsToSync &&
                selectedComponent.componentsToSync.length > 0 &&
                (changeProperties.isAttributeTableChanged || changeProperties.isAttributeFormBuilderChanged)
            )
        ) {
            if (selectedComponent.componentType === "View" || selectedComponent.componentType === "Form") {
                await syncViewAttributesWithComponents(selectedPage._id, selectedComponent.id);
            } else {
                await syncGroupAttributesWithComponents(selectedPage._id, selectedComponent.id);
            }
        }
        return updatedComponent;
    }

    function getReferencesInDbFormat() {
        const referencesInDbFormat = [];
        for (const reference of references) {
            const referenceInDbFormat = {
                relationType: reference.relation._id === "attachment" ? "Attachment" : "Displays",
                id: uuidv4(),
                pageId: reference.page._id,
                entityId: reference.entity._id,
                relationId: reference.relation._id,
                order: reference.order,
                show: reference.show
            };
            if (reference.isNew) {
                reference.order = references.length + 1;
            }
            referencesInDbFormat.push(referenceInDbFormat);
        }
        return referencesInDbFormat;
    }

    return save;
}

export function useUpdateSyncComponentAttributes() {

    const [, pagesDispatcher] = usePages();
    const [{ attributes }, attributeTableDispatcher] = useAttributes();
    const selectedPage = useSelectedPage();
    const pages = usePortalPages();
    const selectedComponent = useSelectedComponent();
    const [{ attributeTable: { attributes: allAttributes } }] = useFetchedValues();

    function getAttributesToUpdate(componentToSync, pageId) {
        let newAttributes = [];
        switch (selectedComponent.componentType) {
            case "View":
            case "Form": {
                const newComponentToSyncAttributes = [];
                const page = getPageByProperty(pages, "_id", pageId);
                for (const attribute of attributes) {
                    if (page.subPageType === "New Record") {
                        const isSystemAttribute = allAttributes.find(attr => attr.id === attribute.attributeId).isSystemAttribute;
                        if (isSystemAttribute === "true" || isSystemAttribute === true) {
                            continue;
                        }
                    }
                    const attributeToSync = componentToSync.attributes.find(attr => attr.attributeId === attribute.attributeId);
                    // if attribute was present in synced component, then update order
                    // else add new attribute
                    if (attributeToSync) {
                        newComponentToSyncAttributes.push(attributeToSync);
                    } else {
                        newComponentToSyncAttributes.push({
                            ...attribute,
                            hyperlink: false,
                            tooltip: true,
                            targetPageId: ""
                        });
                    }
                }
                newAttributes = newComponentToSyncAttributes;
                break;
            }
            default: {
                const newComponentToSyncAttributes = [];
                for (const attribute of attributes) {
                    if (componentToSync.componentType === "Calendar" && newComponentToSyncAttributes.length === 4) {
                        break;
                    }
                    const attributeToSync = componentToSync.attributes.find(attr => attr.attributeId === attribute.attributeId);
                    // if attribute was present in synced component, then update order
                    // else add new attribute
                    if (attributeToSync) {
                        newComponentToSyncAttributes.push(attributeToSync);
                    } else {
                        newComponentToSyncAttributes.push({
                            ...attribute,
                            hyperlink: false,
                            tooltip: true,
                            group: null,
                            groupOrder: null,
                            targetPageId: ""
                        });
                    }
                }
                newAttributes = newComponentToSyncAttributes;
                break;
            }
        }
        if (selectedComponent.componentType === "Calendar") {
            return newAttributes.slice(0, 4);
        }
        return newAttributes;
    }

    function syncAttributesInPages() {
        for (const componentInfo of selectedComponent.componentsToSync) {
            const targetPage = getPageByProperty(pages, "_id", componentInfo.pageId);
            const targetComponent = getComponentByProperty(targetPage.components, "id", componentInfo.componentId);
            let newAttributes = getAttributesToUpdate(targetComponent, componentInfo.pageId);
            pagesDispatcher({
                type: UPDATE_PAGE_COMPONENT,
                payload: {
                    parentId: selectedPage.parentId,
                    pageId: componentInfo.pageId,
                    componentId: componentInfo.componentId,
                    properties: { attributes: newAttributes }
                }
            })
        }
        attributeTableDispatcher.setSyncedComponentsChanged(true);
    }

    return syncAttributesInPages;
}

export function useValidation() {
    const [state, dispatch] = useComponentPropertiesContext();
    const { validation } = state;

    function setValidationErrors(errors) {
        dispatch({
            type: SET_VALIDATION_ERRORS,
            payload: errors
        });
    }

    function clearValidationErrors() {
        dispatch({
            type: CLEAR_VALIDATION_ERRORS
        });
    }

    return [validation, setValidationErrors, clearValidationErrors];
}

export default useComponentPropertiesContext;